import {
  FC, useEffect, useMemo, useState,
} from 'react';
import {
  Box, Button,
  Typography, Divider, Stack, Card, Grid2,
} from '@mui/material';
import { CustomTextField } from '@mgm/common/components/form';
import { useForm, FormProvider } from 'react-hook-form';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { BaseImage } from '@mgm/common/components/base';
import LoadingButton from '@mui/lab/LoadingButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import { useTranslation } from 'react-i18next';
import SvgIcon from '@mui/material/SvgIcon';
import { useGetProductAPI } from '../../../hooks/api/useCommissions';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getSelectedCommissionPlan, getSelectedProduct } from '../../../store/commissions/selectors';
import { commissionsActions } from '../../../store/commissions/slice';
import { getCurrencyByCode } from '../../../store/common/selectors';
import { CreateFixedInput } from './Create/CreateFixedInput';
import { ReactComponent as TrashIcon } from '../../../static/icons/ic_trash.svg';
import { ReactComponent as EditIcon } from '../../../static/icons/ic_edit.svg';
import { TProductVariant } from '../../../types';

type TCommissionSelectProducts = {
  setIsNextDisabled: (val: boolean) => void;
};

export const CommissionSelectProducts: FC<TCommissionSelectProducts> = ({ setIsNextDisabled }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['inputs', 'commissions']);
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<'variantId' | 'sku'>('sku');
  const { pageHeight, resizableDiv } = useScreenResize(80);
  const { mutateAsync: getProductApi, isLoading: isGetProductLoading } = useGetProductAPI();
  const selectedProduct = useAppSelector(getSelectedProduct);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);

  const getCurrency = useAppSelector(getCurrencyByCode);

  useEffect(() => {
    setIsNextDisabled(selectedCommissionPlan.productCommissions?.length === 0);
  }, [setIsNextDisabled, selectedCommissionPlan.productCommissions]);

  const handleSearch = async () => {
    if (search.trim()) {
      await getProductApi({
        [searchType]: search,
        uidType: searchType,
      });
    }
  };

  const handleDelete = (id: string) => {
    const filteredProducts = selectedCommissionPlan.productCommissions.filter((p) => String(p.id) !== String(id));
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      productCommissions: filteredProducts,
    }));
  };

  const methods = useForm({
    mode: 'all',
    values: {
      fixedAmount: selectedProduct.fixedAmount || '',
    },
  });

  const addProduct = () => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      productCommissions: [...selectedCommissionPlan.productCommissions, selectedProduct],
    }));
    dispatch(commissionsActions.setSelectedProduct({} as TProductVariant));
    setSearch('');
  };

  const handleFormChange = (key: string, val: string | number) => {
    if (key === 'fixedType') {
      methods.reset({
        fixedAmount: '',
      });
    }

    dispatch(
      commissionsActions.setSelectedProduct({
        ...selectedProduct,
        [key]: val,
      }),
    );
  };

  const editProduct = (product: TProductVariant) => {
    dispatch(commissionsActions.setSelectedProduct(product));
  };

  const isProductExists = useMemo(() => selectedCommissionPlan.productCommissions?.find((prod) => String(prod.id) === String(selectedProduct.id)), [selectedCommissionPlan, selectedProduct]);

  const handleProduct = () => {
    if (isProductExists) {
      const updatedProductCommissions = selectedCommissionPlan.productCommissions.map((product) => (product.id === selectedProduct.id ? selectedProduct : product));

      dispatch(commissionsActions.setSelectedCommissionsPlan({
        ...selectedCommissionPlan,
        productCommissions: updatedProductCommissions,
      }));

      dispatch(commissionsActions.setSelectedProduct({} as TProductVariant));
      setSearch('');
    } else {
      addProduct();
    }
  };

  return (
    <div ref={resizableDiv}>
      <Card sx={{
        mt: 2, display: 'flex', p: 2, mx: 3, gap: 2, height: pageHeight,
      }}
      >
        <FormProvider {...methods}>
          <Box sx={{ flex: 1 }}>
            <Stack pb={2} alignItems="center" direction="row" spacing={2}>
              <Box flexGrow={1}>
                <CustomTextField
                  onChange={(id) => setSearch(id)}
                  isDisplayLabel={false}
                  placeholder={`${t('enterVariantIDOrSKU', { ns: 'commissions' })}...`}
                  name="search"
                />
              </Box>
              <LoadingButton loading={isGetProductLoading} disabled={!search.trim() || isGetProductLoading} variant="contained" onClick={handleSearch}>
                {t('search')}
              </LoadingButton>
            </Stack>
            <Stack pb={2} alignItems="center" direction="row" spacing={2}>
              <FormLabel component="legend">
                {t('searchBy', { ns: 'commissions' })}
                :
              </FormLabel>
              <RadioGroup
                row
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'variantId' | 'sku')}
              >
                <FormControlLabel value="sku" control={<Radio />} label="SKU" />
                <FormControlLabel value="variantId" control={<Radio />} label={t('variantId', { ns: 'commissions' })} />
              </RadioGroup>
            </Stack>
            <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />
            {selectedProduct?.id ? (
              <Box p={2} borderRadius={1}>
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size={{
                    xs: 12,
                    lg: 12,
                    md: 12,
                    sm: 12,
                  }}
                  >
                    <Stack direction="row" spacing={2}>
                      <BaseImage
                        disabledEffect
                        visibleByDefault
                        alt={selectedProduct?.image?.altText || 'Product'}
                        src={selectedProduct?.image?.url}
                        sx={{ borderRadius: 1.5, width: 175, height: 175 }}
                      />

                      <Stack direction="column" spacing={1} justifyContent="center">
                        <Typography variant="body2">
                          <strong>
                            {' '}
                            {t('productName', { ns: 'commissions' })}
                            :
                          </strong>
                          {' '}
                          {selectedProduct?.displayName || '/'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>
                            {' '}
                            {t('category', { ns: 'commissions' })}
                            :
                          </strong>
                          {' '}
                          {selectedProduct?.product?.category?.name || '/'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>
                            {t('variantId', { ns: 'commissions' })}
                            :
                          </strong>
                          {' '}
                          {selectedProduct?.id?.split('/').pop() || '/'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>SKU:</strong>
                          {' '}
                          {selectedProduct?.sku || '/'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>
                            {t('price', { ns: 'commissions' })}
                            :
                          </strong>
                          {' '}
                          {selectedProduct?.price || '/'}
                          {' '}
                          {getCurrency()?.mark || ''}
                        </Typography>
                      </Stack>
                    </Stack>

                  </Grid2>
                </Grid2>

                <Box mt={2}>
                  <CreateFixedInput isCommissionPerProduct handleFormChange={handleFormChange} />
                </Box>
                <Box mt={2}>
                  <Button disabled={!selectedProduct.fixedAmount} size="small" variant="outlined" onClick={handleProduct}>
                    {isProductExists ? t('editCommissionProduct', { ns: 'commissions' }) : t('addCommissionProduct', { ns: 'commissions' })}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                flexGrow={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <img src="/searchProduct.png" alt="No product preview" height={450} />
              </Box>
            )}
          </Box>
        </FormProvider>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" mb={2}>
            {t('selectedProducts', { ns: 'commissions' })}
          </Typography>
          <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />

          {selectedCommissionPlan.productCommissions?.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={4}>
              <img src="/noProducts.png" alt="No products" width={150} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                {t('noProductsSelected', { ns: 'commissions' })}
              </Typography>
            </Box>
          ) : (
            <Box>
              {selectedCommissionPlan.productCommissions?.map((product) => (
                <Box
                  key={product.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  my={1}
                  p={1.5}
                  border={1}
                  borderColor="grey.300"
                  borderRadius={1}
                >
                  <BaseImage
                    disabledEffect
                    visibleByDefault
                    alt={product?.image?.altText || 'Product'}
                    src={product?.image?.url}
                    sx={{ borderRadius: 1.5, width: 55, height: 55 }}
                  />
                  <Box flex={1}>
                    <Typography variant="body1">{product.displayName}</Typography>
                    <Typography variant="body2">
                      <strong>ID:</strong>
                      {' '}
                      {product?.id?.split('/').pop() || '/'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Price:</strong>
                      {' '}
                      {product.price || '/'}
                      {getCurrency()?.mark}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{`${product.fixedType === 0 ? t('flat', { ns: 'commissions' }) : t('percentage', { ns: 'commissions' })}:`}</strong>
                      {' '}
                      {' '}
                      {product.fixedAmount}
                      {product.fixedType === 0 ? getCurrency()?.mark : '%'}
                    </Typography>
                  </Box>
                  <Box onClick={() => editProduct(product)}>
                    <SvgIcon
                      className="cursor-pointer"
                      component={EditIcon}
                      inheritViewBox
                    />
                  </Box>
                  <Box onClick={() => handleDelete(product.id)}>
                    <SvgIcon
                      className="cursor-pointer"
                      component={TrashIcon}
                      inheritViewBox
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Card>
    </div>
  );
};
